import React from 'react';
import { Card, CardContent, Box, Typography, Skeleton, Stack } from '@mui/material';

/**
 * A titled panel with consistent header treatment, used for every chart and
 * content block. Handles its own loading and empty states so pages stop
 * repeating the `loading ? spinner : empty ? message : content` ternary.
 */
const Panel = ({
  title,
  subtitle,
  action,
  children,
  loading = false,
  isEmpty = false,
  emptyMessage = 'No data available',
  bodyHeight,
  dense = false,
  sx,
}) => (
  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', ...sx }}>
    <CardContent
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        ...(dense ? { p: 2, '&:last-child': { pb: 2 } } : {}),
      }}
    >
      {(title || action) && (
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: subtitle ? 0.5 : 2 }}
        >
          <Box sx={{ minWidth: 0 }}>
            {title && (
              <Typography variant="h6" component="h2" noWrap>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
        </Stack>
      )}

      {subtitle && <Box sx={{ mb: 1.5 }} />}

      <Box
        sx={{
          flex: 1,
          position: 'relative',
          ...(bodyHeight ? { height: bodyHeight } : {}),
        }}
      >
        {loading ? (
          <Skeleton
            variant="rounded"
            animation="wave"
            sx={{ height: bodyHeight || '100%', minHeight: 120, bgcolor: 'rgba(148,163,184,0.08)' }}
          />
        ) : isEmpty ? (
          <Box
            sx={{
              height: '100%',
              minHeight: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {emptyMessage}
            </Typography>
          </Box>
        ) : (
          children
        )}
      </Box>
    </CardContent>
  </Card>
);

export default Panel;
